import { useState, useMemo } from "react";
import { Box, Typography, TextField, Button, Alert } from "@mui/material";
import { format } from "date-fns";
import { useUpdateRecord } from "../../../api/useUpdateRecord";
import { useRecords } from "../../../api/useRecords";
import { useQueryClient } from "@tanstack/react-query";
import { StaffWeeklyHours } from "../types";
import { computeCumulativeHours } from "../utils/calculations";
import { createWorklogs } from "../utils/useWorkLogs";
import { createTimeOffEntries, formatSaldo } from "../utils/general";
import { detectSnapshotDrift, StaffHoursSnapshot } from "../utils/snapshots";

const DRIFT_TOLERANCE_HOURS = 1;

type SnapshotManagerProps = {
    snapshot: StaffHoursSnapshot;
    recheckWindowStart: Date;
    checkpoint: Date;
    priorBalanceForRecheck: number;
    userId: string;
    weeklyWorkHoursSchedule: Array<StaffWeeklyHours>;
    publicHolidaysSet: Set<string>;
    employmentStartDate: Date | null;
};

// Admin panel to override the carried-forward snapshot balance, and an on-demand drift check that
// re-fetches the closed period (corrections/extra-hours-paid are not re-applied — those are rare manual
// entries; this targets silent schedule/time-off changes).
export const SnapshotManager = ({
    snapshot,
    recheckWindowStart,
    checkpoint,
    priorBalanceForRecheck,
    userId,
    weeklyWorkHoursSchedule,
    publicHolidaysSet,
    employmentStartDate,
}: SnapshotManagerProps) => {
    const updateSnapshot = useUpdateRecord();
    const queryClient = useQueryClient();
    const [manualBalance, setManualBalance] = useState(snapshot.manual_balance != null ? String(snapshot.manual_balance) : "");
    const [note, setNote] = useState(snapshot.note ?? "");
    const [rechecking, setRechecking] = useState(false);

    const rangeFilter = {
        user: { id: { _eq: userId } },
        start_date: { _gte: recheckWindowStart.toISOString(), _lte: checkpoint.toISOString() },
    };

    const { data: recheckSchedules, status: recheckSchedulesStatus } = useRecords(
        "schedules",
        "id,start_date,end_date,user.id",
        { extraOptions: { filter: rangeFilter, pageSize: -1 }, enabled: rechecking }
    );
    const { data: recheckTimeOff, status: recheckTimeOffStatus } = useRecords(
        "time_off",
        "*.*",
        { extraOptions: { filter: rangeFilter, pageSize: -1 }, enabled: rechecking }
    );

    const driftResult = useMemo(() => {
        if (!rechecking || recheckSchedulesStatus !== "success" || recheckTimeOffStatus !== "success") return null;
        const workLogs = createWorklogs([], recheckSchedules.items);
        const timeOffs = createTimeOffEntries(recheckTimeOff.items);
        const { lifetimeSaldo } = computeCumulativeHours({
            workLogs,
            timeOffs,
            toDate: checkpoint,
            weeklyWorkHoursSchedule,
            publicHolidaysSet,
            expectedHoursCorrection: 0,
            extraHoursPaid: 0,
            cumulativeStartDate: recheckWindowStart,
            employmentStartDate,
            priorBalance: priorBalanceForRecheck,
        });
        const drift = detectSnapshotDrift(snapshot.computed_balance, lifetimeSaldo, DRIFT_TOLERANCE_HOURS);
        return { recomputed: lifetimeSaldo, drift };
    }, [
        rechecking,
        recheckSchedulesStatus,
        recheckTimeOffStatus,
        recheckSchedules,
        recheckTimeOff,
        checkpoint,
        weeklyWorkHoursSchedule,
        publicHolidaysSet,
        recheckWindowStart,
        employmentStartDate,
        priorBalanceForRecheck,
        snapshot.computed_balance,
    ]);

    const handleSave = async () => {
        const value = manualBalance.trim() === "" ? null : Number(manualBalance);
        await updateSnapshot.mutateAsync({ entity: "staff_hours_snapshots", id: snapshot.id, data: { manual_balance: value, note } });
        await queryClient.invalidateQueries({ predicate: (query: any) => query.queryKey[0]?.includes?.("staff_hours_snapshots") });
    };

    return (
        <Box sx={{ mt: 2, p: 2, border: 1, borderColor: "divider", borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
                Gerir saldo de anos anteriores (até {format(checkpoint, "dd/MM/yyyy")})
            </Typography>
            <Typography variant="body2" color="text.secondary">
                Calculado automaticamente: {formatSaldo(Number(snapshot.computed_balance))}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1, flexWrap: "wrap" }}>
                <TextField
                    label="Ajuste manual (h)"
                    size="small"
                    type="number"
                    value={manualBalance}
                    onChange={(event) => setManualBalance(event.target.value)}
                    sx={{ width: 160 }}
                />
                <TextField
                    label="Nota"
                    size="small"
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                    sx={{ flex: 1, minWidth: 200 }}
                />
                <Button variant="contained" size="small" onClick={handleSave}>
                    Guardar
                </Button>
                <Button variant="outlined" size="small" onClick={() => setRechecking(true)}>
                    Recalcular
                </Button>
            </Box>
            {driftResult &&
                (driftResult.drift ? (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                        Os dados do período fechado mudaram: guardado {formatSaldo(Number(snapshot.computed_balance))}, recalculado{" "}
                        {formatSaldo(driftResult.recomputed)}. Considere atualizar o snapshot.
                    </Alert>
                ) : (
                    <Alert severity="success" sx={{ mt: 1 }}>
                        Sem alterações no período fechado (recalculado {formatSaldo(driftResult.recomputed)}).
                    </Alert>
                ))}
        </Box>
    );
};
